import { concat } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButtonGroup } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import { t } from 'ember-intl';
import {
  type Variance,
  VARIANCES,
} from 'lumen/utils/constants/budget-forecast-constants';

interface HotelBudgetForecastVarianceOptionsSignature {
  Args: {
    onChange: (variance: Variance) => void;
    selectedVariance: Variance;
  };
}

export default class HotelBudgetForecastVarianceOptions extends Component<HotelBudgetForecastVarianceOptionsSignature> {
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  get options(): {
    description: string;
    label: string;
    title: string;
    value: Variance;
  }[] {
    return [
      {
        value: VARIANCES.PERCENTAGE,
        title: this.intl.t('budget_forecast.variance.percentage.title'),
        description: this.intl.t(
          'budget_forecast.variance.percentage.description',
        ),
        label: '%',
      },
      {
        value: VARIANCES.ABSOLUTE,
        title: this.intl.t('budget_forecast.variance.absolute.title'),
        description: this.intl.t(
          'budget_forecast.variance.absolute.description',
        ),
        label: '#',
      },
      {
        value: VARIANCES.NONE,
        title: this.intl.t('budget_forecast.variance.none.title'),
        description: this.intl.t('budget_forecast.variance.none.description'),
        label: '–',
      },
    ] as const;
  }

  @action
  handleVarianceChange(variance: string | string[]): void {
    if (Array.isArray(variance)) {
      return;
    }

    this.usageAnalytics.track('Variance Type Edited', {
      eventTrigger: 'variance-button-clicked',
      varianceType: variance,
    });
    this.args.onChange(variance as Variance);
  }

  <template>
    <div
      class="ic-product-tour-48864127-variance-options flex items-center gap-100"
    >
      <span class="text-300--medium text-neutral-subdued">
        {{t "general.variance"}}:
      </span>
      <PrismButtonGroup
        @onChange={{this.handleVarianceChange}}
        @value={{@selectedVariance}}
        as |ButtonGroup|
      >
        {{#each this.options as |option|}}
          <ButtonGroup.button
            @label={{option.label}}
            @value={{option.value}}
            id={{concat "variance-" option.value}}
          />
          <EmberTooltip
            @side="top"
            @targetId={{concat "variance-" option.value}}
            @tooltipClass="prism-tooltip"
          >
            <p class="text-200--bold mb-100">
              {{option.title}}
            </p>
            <p class="paragraph-200--regular">
              {{option.description}}
            </p>
          </EmberTooltip>
        {{/each}}
      </PrismButtonGroup>
    </div>
  </template>
}
